import { api, API_BASE } from "./client";

/**
 * Phase 6.1 Hito 1+2 — Inventory API client.
 */
function buildParams(filters = {}, extra = {}) {
  const p = {};
  if (filters.preset && filters.preset !== "custom") {
    p.preset = filters.preset;
  } else if (filters.preset === "custom") {
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
  }
  if (filters.source_report) p.source_reports = filters.source_report;
  if (filters.transaction_types?.length) {
    p.canonical_types = filters.transaction_types.join(",");
  }
  if (filters.include_non_warehouse) p.include_non_warehouse = true;
  return { ...p, ...extra };
}

async function _downloadBlob(path, params, filenamePrefix) {
  const resp = await api.get(path, { params, responseType: "blob" });
  const blob = new Blob([resp.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "").replace(/-/g, "");
  a.href = url;
  a.download = `${filenamePrefix}_${ts}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const inventory = {
  // ── Hito 1 ───────────────────────────────────────────────────────
  async getKpis(filters = {}, extra = {}) {
    const params = buildParams(filters, extra);
    const { data } = await api.get("/inventory/kpis", { params });
    return data;
  },
  async getSummaryTable(filters = {}, opts = {}) {
    const params = buildParams(filters, {
      page: opts.page || 1,
      page_size: opts.pageSize || 50,
      sort_by: opts.sortBy || "salidas_qty",
      sort_dir: opts.sortDir || "desc",
      search: opts.search || undefined,
    });
    const { data } = await api.get("/inventory/summary-table", { params });
    return data;
  },
  buildExportUrl(filters = {}, opts = {}) {
    const params = buildParams(filters, {
      sort_by: opts.sortBy || "salidas_qty",
      sort_dir: opts.sortDir || "desc",
      search: opts.search || undefined,
    });
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    ).toString();
    return `${API_BASE}/inventory/summary-table/export?${qs}`;
  },
  async downloadExport(filters = {}, opts = {}) {
    const params = buildParams(filters, {
      sort_by: opts.sortBy || "salidas_qty",
      sort_dir: opts.sortDir || "desc",
      search: opts.search || undefined,
    });
    await _downloadBlob("/inventory/summary-table/export", params, "inventario_resumen");
  },
  async getNeedsReview({ page = 1, pageSize = 50, canonicalType = null } = {}) {
    const params = { page, page_size: pageSize };
    if (canonicalType) params.canonical_type = canonicalType;
    const { data } = await api.get("/inventory/needs-review", { params });
    return data;
  },

  // ── Hito 2 ───────────────────────────────────────────────────────
  async getMonthlyPivot(filters = {}, opts = {}) {
    const params = buildParams(filters, {
      page: opts.page || 1,
      page_size: opts.pageSize || 50,
      sort_by: opts.sortBy || "total_year",
      sort_dir: opts.sortDir || "desc",
      search: opts.search || undefined,
    });
    const { data } = await api.get("/inventory/monthly-pivot", { params });
    return data;
  },
  async downloadMonthlyPivotExport(filters = {}, opts = {}) {
    const params = buildParams(filters, {
      sort_by: opts.sortBy || "total_year",
      sort_dir: opts.sortDir || "desc",
      search: opts.search || undefined,
    });
    await _downloadBlob("/inventory/monthly-pivot/export", params, "inventario_pivot_mensual");
  },

  async getDeadStock({ filters = {}, page = 1, pageSize = 50, sortBy = "dias_sin_movimiento", sortDir = "desc", search, thresholdDays = 90 } = {}) {
    const params = buildParams(filters, {
      page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir, search: search || undefined,
      threshold_days: thresholdDays,
    });
    // Dead-stock NO usa canonical_types ni preset (siempre HOY).
    delete params.canonical_types;
    delete params.preset;
    delete params.date_from;
    delete params.date_to;
    const { data } = await api.get("/inventory/dead-stock", { params });
    return data;
  },
  async downloadDeadStockExport({ filters = {}, sortBy, sortDir, search, thresholdDays = 90 } = {}) {
    const params = buildParams(filters, {
      sort_by: sortBy || "dias_sin_movimiento",
      sort_dir: sortDir || "desc",
      search: search || undefined,
      threshold_days: thresholdDays,
    });
    delete params.canonical_types;
    delete params.preset;
    delete params.date_from;
    delete params.date_to;
    await _downloadBlob("/inventory/dead-stock/export", params, "inventario_dead_stock");
  },

  async getUserMovements(filters = {}, opts = {}) {
    const params = buildParams(filters, {
      page: opts.page || 1,
      page_size: opts.pageSize || 50,
      sort_by: opts.sortBy || "total_tx",
      sort_dir: opts.sortDir || "desc",
      search: opts.search || undefined,
    });
    const { data } = await api.get("/inventory/user-movements", { params });
    return data;
  },
  async downloadUserMovementsExport(filters = {}, opts = {}) {
    const params = buildParams(filters, {
      sort_by: opts.sortBy || "total_tx",
      sort_dir: opts.sortDir || "desc",
      search: opts.search || undefined,
    });
    await _downloadBlob("/inventory/user-movements/export", params, "inventario_usuarios");
  },

  async getTopProducts(filters = {}, opts = {}) {
    const params = buildParams(filters, {
      metric: opts.metric || "qty",
      top_n: opts.topN || 25,
    });
    const { data } = await api.get("/inventory/top-products", { params });
    return data;
  },
  async downloadTopProductsExport(filters = {}, opts = {}) {
    const params = buildParams(filters, {
      metric: opts.metric || "qty",
      top_n: opts.topN || 25,
    });
    await _downloadBlob("/inventory/top-products/export", params, "inventario_top_productos");
  },

  async getChartsData(filters = {}) {
    const params = buildParams(filters);
    const { data } = await api.get("/inventory/charts-data", { params });
    return data;
  },

  // ── Phase 8.1 — Outbound Report (Reporte de Salidas) ─────────────
  async getOutboundReport(filters = {}, opts = {}) {
    const params = buildParams(filters, {
      top_n: opts.topN || 20,
      metric_order_by: opts.metricOrderBy || "qty",
    });
    // GENDETAILED_PROD has no outbound (stock_delta<0). When the user has
    // the global default GENDETAILED selected, we drop source_report so the
    // endpoint aggregates across all sources and returns the IC385 outbound.
    if (params.source_reports === "GENDETAILED_PROD") {
      delete params.source_reports;
    } else if (params.source_reports) {
      // Backend expects `source_report` (singular, repeated). Rename.
      params.source_report = params.source_reports;
      delete params.source_reports;
    }
    const { data } = await api.get("/inventory/outbound-report", { params });
    return data;
  },
};

export default inventory;
